import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getApolloErrorMessageCodes, getApolloErrorMessages } from '../../lib/errors';
import Button from '../Button';
import Card from '../Card';
import { FormStatus } from '../FormStatus';
import Input from '../Input';
import PageSeo, { meta } from '../PageSeo';
import PortalLayout from '../PortalLayout';
import TextLink from '../TextLink';
import TwoFactorCodeUsedMessage, { getNewCodeInSeconds } from '../TwoFactorCodeUsedMessage';
import { completeLogin } from './completeLogin';
import { TWO_FACTOR_CHALLENGE_EXPIRED, verifyTwoFactorLogin } from './twoFactorMutations';

export default function TwoFactorChallenge() {
  const { search } = useLocation();
  const [isRecoveryCode, setIsRecoveryCode] = useState(false);
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<React.ReactNode>();
  const [isExpired, setIsExpired] = useState(false);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setError(undefined);
      setIsSubmitting(true);

      try {
        const { user, userHash } = await verifyTwoFactorLogin({ code: code.trim() });
        completeLogin({ id: user.id, email: user.email, userHash });
      } catch (e) {
        setIsSubmitting(false);
        setCode('');
        if (getApolloErrorMessageCodes(e)?.includes(TWO_FACTOR_CHALLENGE_EXPIRED)) {
          setIsExpired(true);
          return;
        }
        const newCodeInSeconds = getNewCodeInSeconds(e);
        setError(
          newCodeInSeconds === null ? (
            getApolloErrorMessages(e) || (e instanceof Error ? e.message : 'Unable to verify that code')
          ) : (
            <TwoFactorCodeUsedMessage newCodeInSeconds={newCodeInSeconds} />
          ),
        );
      }
    },
    [code],
  );

  const toggleCodeType = useCallback(() => {
    setIsRecoveryCode((current) => !current);
    setCode('');
    setError(undefined);
  }, []);

  return (
    <PortalLayout>
      <div className="mx-auto mt-10 px-4 rounded text-center">
        <PageSeo title={meta.LOGIN.title} description={meta.LOGIN.description} />
        <Card className="py-4 px-6 max-w-lg mx-auto">
          <div className="my-5 mb-8">
            <h1 className="text-3xl">Two-factor authentication</h1>
          </div>

          {isExpired ? (
            <FormStatus type="error">
              Your sign-in has expired. Please <TextLink to={`/login${search}`}>sign in again</TextLink>.
            </FormStatus>
          ) : (
            <>
              {error && <FormStatus type="error">{error}</FormStatus>}
              <p className="mb-4">
                {isRecoveryCode
                  ? 'Enter one of the recovery codes you saved when you turned on two-factor authentication.'
                  : 'Enter the 6-digit code from your authenticator app.'}
              </p>
              <form onSubmit={handleSubmit}>
                <Input
                  key={isRecoveryCode ? 'recovery' : 'authenticator'}
                  className="mb-4"
                  name="code"
                  aria-label={isRecoveryCode ? 'Recovery code' : 'Authentication code'}
                  placeholder={isRecoveryCode ? 'xxxxx-xxxxx' : '123456'}
                  autoComplete="one-time-code"
                  inputMode={isRecoveryCode ? 'text' : 'numeric'}
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  initiallyFocused
                />
                <div className="mb-4">
                  <Button
                    size="lg"
                    fullWidth
                    type="submit"
                    loading={isSubmitting}
                    disabled={!code.trim() || isSubmitting}
                  >
                    Verify
                  </Button>
                </div>
              </form>
              <div className="mb-4">
                <TextLink type="button" onClick={toggleCodeType}>
                  {isRecoveryCode ? 'Use your authenticator app instead' : 'Use a recovery code instead'}
                </TextLink>
              </div>
            </>
          )}
        </Card>
      </div>
    </PortalLayout>
  );
}
